import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, EmptyState, Icon, IconButton, Input, Modal, Pill, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import "./lifecycle.css";

/**
 * The project and task lifecycle: templates, archive, and the trash.
 *
 * Kept in one file, apart from Work and ProjectDetail, so those pages only
 * mount a component each rather than growing the lifecycle's state.
 */

const dateFormat = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });
const formatDate = (iso: string) => dateFormat.format(new Date(iso));

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/* ------------------------------------------------------------------ */
/* Project templates                                                   */
/* ------------------------------------------------------------------ */

export interface ProjectTemplateCard {
  id: string;
  name: string;
  description: string | null;
  sectionCount: number;
  milestoneCount: number;
  taskCount: number;
  canManage: boolean;
}

/** A small gallery in the new-project dialog; choosing none starts from blank. */
export function ProjectTemplateGallery({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (templateId: string | null) => void;
}) {
  const [templates, setTemplates] = useState<ProjectTemplateCard[]>([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ templates: ProjectTemplateCard[] }>("/project-templates?limit=12")
      .then((res) => !cancelled && setTemplates(res.templates))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // No templates yet: the dialog is exactly what it was.
  if (templates.length === 0) return null;

  return (
    <fieldset className="lifecycle-gallery">
      <legend className="lifecycle-gallery__legend">Start from</legend>
      <div className="lifecycle-gallery__grid" role="radiogroup" aria-label="Project template">
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          className={`lifecycle-gallery__card ${value === null ? "is-selected" : ""}`}
          onClick={() => onChange(null)}
        >
          <strong>Blank project</strong>
          <span>Nothing carried over</span>
        </button>
        {templates.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={value === t.id}
            className={`lifecycle-gallery__card ${value === t.id ? "is-selected" : ""}`}
            onClick={() => onChange(t.id)}
          >
            <strong>{t.name}</strong>
            <span>
              {t.taskCount} tasks · {t.sectionCount} sections · {t.milestoneCount} milestones
            </span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Names the template, then saves the project (or task) behind `path` as one. */
export function SaveTemplateDialog({
  open,
  onClose,
  path,
  defaultName,
  withMembers = false,
  noun,
}: {
  open: boolean;
  onClose: () => void;
  /** e.g. `/projects/:id/save-as-template` */
  path: string;
  defaultName: string;
  /** Projects only: offer to keep the default members' roles. */
  withMembers?: boolean;
  noun: "project" | "task";
}) {
  const { toast } = useToast();
  const [name, setName] = useState(defaultName);
  const [includeMembers, setIncludeMembers] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setIncludeMembers(false);
    }
  }, [open, defaultName]);

  const save = () => {
    if (!name.trim()) return;
    setSaving(true);
    api
      .post(path, { name: name.trim(), ...(withMembers ? { includeMembers } : {}) })
      .then(() => {
        toast(`Saved as a ${noun} template.`);
        onClose();
      })
      .catch((error: unknown) => toast(message(error, "Could not save the template."), { tone: "danger" }))
      .finally(() => setSaving(false));
  };

  return (
    <Modal open={open} onClose={onClose} title={`Save ${noun} as template`}>
      <form
        className="lifecycle-form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Input label="Template name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
        {withMembers && (
          <label className="lifecycle-form__check">
            <input type="checkbox" checked={includeMembers} onChange={(e) => setIncludeMembers(e.target.checked)} />
            Keep the members and their roles
          </label>
        )}
        <p className="lifecycle-form__hint">
          {noun === "project"
            ? "Sections, columns, milestones, tasks and checklists are saved. Dates are kept as offsets from the project start."
            : "The brief, checklist, tags and priority are saved."}
        </p>
        <div className="lifecycle-form__actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!name.trim()}>
            Save template
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Task templates                                                      */
/* ------------------------------------------------------------------ */

export interface TaskTemplateCard {
  id: string;
  name: string;
  title: string;
  description: string | null;
  priority: string;
  estimateMinutes: number | null;
  checklistCount: number;
  tags: string[];
}

/** A compact chooser for the new-task dialog. Renders nothing without templates. */
export function TaskTemplatePicker({
  value,
  onPick,
}: {
  value: string | null;
  onPick: (template: TaskTemplateCard | null) => void;
}) {
  const [templates, setTemplates] = useState<TaskTemplateCard[]>([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ templates: TaskTemplateCard[] }>("/task-templates?limit=50")
      .then((res) => !cancelled && setTemplates(res.templates))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (templates.length === 0) return null;

  return (
    <label className="lifecycle-taskpicker">
      <span>From template</span>
      <select value={value ?? ""} onChange={(e) => onPick(templates.find((t) => t.id === e.target.value) ?? null)}>
        <option value="">None</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
            {t.checklistCount ? ` (${t.checklistCount} checklist items)` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Trash                                                               */
/* ------------------------------------------------------------------ */

export interface TrashedTask {
  id: string;
  title: string;
  projectId: string | null;
  projectCode: string | null;
  projectName: string | null;
  projectDeleted: boolean;
  deletedAt: string;
  deletedByName: string | null;
  purgeAt: string;
  subtaskCount: number;
  canRestore: boolean;
}

/** Deleted tasks, newest first, with the date each is purged for good. */
export function TrashList({ projectId, mine, onRestored }: { projectId?: string; mine?: boolean; onRestored?: () => void }) {
  const { toast } = useToast();
  const [tasks, setTasks] = useState<TrashedTask[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const query = useCallback(
    (after?: string | null) => {
      const params = new URLSearchParams({ limit: "25" });
      if (projectId) params.set("projectId", projectId);
      if (mine) params.set("mine", "true");
      if (after) params.set("cursor", after);
      return api.get<{ tasks: TrashedTask[]; nextCursor: string | null }>(`/tasks/trash?${params}`);
    },
    [projectId, mine],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    query()
      .then((res) => {
        if (cancelled) return;
        setTasks(res.tasks);
        setCursor(res.nextCursor);
      })
      .catch((error: unknown) => toast(message(error, "Could not load the trash."), { tone: "danger" }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query, toast]);

  const more = () => {
    if (!cursor) return;
    query(cursor)
      .then((res) => {
        setTasks((prev) => [...prev, ...res.tasks]);
        setCursor(res.nextCursor);
      })
      .catch((error: unknown) => toast(message(error, "Could not load more."), { tone: "danger" }));
  };

  const restore = (task: TrashedTask) => {
    setBusyId(task.id);
    api
      .post(`/tasks/${task.id}/restore`, {})
      .then(() => {
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        toast(`Restored "${task.title}".`);
        onRestored?.();
      })
      .catch((error: unknown) => {
        // PROJECT_DELETED and PARENT_TRASHED carry a plain-language message.
        toast(message(error, "Could not restore the task."), { tone: "danger" });
      })
      .finally(() => setBusyId(null));
  };

  if (loading) return <p className="lifecycle-trash__empty">Loading…</p>;
  if (tasks.length === 0) {
    return <EmptyState icon="trash" title="Trash is empty" description="Deleted tasks stay here for 30 days." size="inline" />;
  }

  return (
    <div className="lifecycle-trash">
      <ul className="lifecycle-trash__list">
        {tasks.map((task) => (
          <li key={task.id} className="lifecycle-trash__row">
            <div className="lifecycle-trash__main">
              <span className="lifecycle-trash__title">{task.title}</span>
              <span className="lifecycle-trash__meta">
                {task.projectCode && <Pill tone="neutral">{task.projectCode}</Pill>}
                {task.projectDeleted && <Pill tone="warn">Project deleted</Pill>}
                <span>
                  Deleted {formatDate(task.deletedAt)}
                  {task.deletedByName ? ` by ${task.deletedByName}` : ""}
                </span>
                {task.subtaskCount > 0 && <span>{task.subtaskCount} subtasks</span>}
                <span>Removed for good {formatDate(task.purgeAt)}</span>
              </span>
            </div>
            {task.canRestore && (
              <Button variant="secondary" size="sm" loading={busyId === task.id} onClick={() => restore(task)}>
                Restore
              </Button>
            )}
          </li>
        ))}
      </ul>
      {cursor && (
        <Button variant="ghost" size="sm" onClick={more}>
          Show more
        </Button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Archive                                                             */
/* ------------------------------------------------------------------ */

interface ArchivedProject {
  id: string;
  code: string;
  name: string;
  archivedAt: string | null;
}

/** Sidebar entry point: archived projects, and "my deleted tasks". */
export function LifecycleLauncher() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"archived" | "trash">("archived");
  const [projects, setProjects] = useState<ArchivedProject[] | null>(null);

  useEffect(() => {
    if (!open || tab !== "archived") return;
    let cancelled = false;
    api
      .get<{ projects: ArchivedProject[] }>("/projects?archived=only")
      .then((res) => !cancelled && setProjects(res.projects))
      .catch(() => !cancelled && setProjects([]));
    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  return (
    <>
      <IconButton icon={<Icon name="archive" size={16} />} label="Archived projects and trash" size="sm" onClick={() => setOpen(true)} />
      <Modal open={open} onClose={() => setOpen(false)} title="Archive and trash">
        <div className="lifecycle-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "archived"} onClick={() => setTab("archived")}>
            Archived projects
          </button>
          <button type="button" role="tab" aria-selected={tab === "trash"} onClick={() => setTab("trash")}>
            My deleted tasks
          </button>
        </div>
        {tab === "archived" ? (
          projects === null ? (
            <p className="lifecycle-trash__empty">Loading…</p>
          ) : projects.length === 0 ? (
            <EmptyState icon="archive" title="No archived projects" size="inline" />
          ) : (
            <ul className="lifecycle-trash__list">
              {projects.map((p) => (
                <li key={p.id} className="lifecycle-trash__row">
                  <Link to={`/work/project/${p.id}`} onClick={() => setOpen(false)}>
                    {p.code} · {p.name}
                  </Link>
                  {p.archivedAt && <span className="lifecycle-trash__meta">Archived {formatDate(p.archivedAt)}</span>}
                </li>
              ))}
            </ul>
          )
        ) : (
          <TrashList mine />
        )}
      </Modal>
    </>
  );
}

/** Project header actions: save as template, trash, archive or restore. */
export function ProjectLifecycleActions({
  projectId,
  projectName,
  archived,
  canManage,
  canArchive,
  onChanged,
}: {
  projectId: string;
  projectName: string;
  archived: boolean;
  canManage: boolean;
  canArchive: boolean;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [saveOpen, setSaveOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleArchive = () => {
    setBusy(true);
    api
      .post(`/projects/${projectId}/${archived ? "unarchive" : "archive"}`, {})
      .then(() => {
        toast(archived ? "Project restored from the archive." : "Project archived. It is now read-only.");
        onChanged();
      })
      .catch((error: unknown) => toast(message(error, "Could not change the archive state."), { tone: "danger" }))
      .finally(() => setBusy(false));
  };

  return (
    <>
      {canManage && (
        <IconButton icon={<Icon name="copy" size={15} />} label="Save as template" onClick={() => setSaveOpen(true)} />
      )}
      <IconButton icon={<Icon name="restore" size={15} />} label="Deleted tasks" variant="ghost" onClick={() => setTrashOpen(true)} />
      {canArchive && (
        <Button variant="ghost" size="sm" icon={<Icon name="archive" size={14} />} loading={busy} onClick={toggleArchive}>
          {archived ? "Restore project" : "Archive"}
        </Button>
      )}
      <SaveTemplateDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        path={`/projects/${projectId}/save-as-template`}
        defaultName={projectName}
        withMembers
        noun="project"
      />
      <Modal open={trashOpen} onClose={() => setTrashOpen(false)} title="Deleted tasks">
        <TrashList projectId={projectId} onRestored={onChanged} />
      </Modal>
    </>
  );
}

export function ArchivedBanner({ archivedAt }: { archivedAt: string | null }) {
  return (
    <div className="lifecycle-banner" role="status">
      <Icon name="archive" size={14} />
      This project is archived{archivedAt ? ` (${formatDate(archivedAt)})` : ""} and read-only.
    </div>
  );
}
